import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Inventory as ProductsIcon,
  Search as SearchIcon,
  Image as ImageIcon,
  Visibility as ViewIcon,
  AttachMoney as PriceIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  GetApp as DownloadIcon,
  DeleteForever as DeleteForeverIcon,
  Clear as ClearIcon,
  Restore as RestoreIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const ProductManagement = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [tempCategoryValue, setTempCategoryValue] = useState('');
  const [editingBrand, setEditingBrand] = useState(null);
  const [tempBrandValue, setTempBrandValue] = useState('');
  const [editingWeight, setEditingWeight] = useState(null);
  const [tempWeightValue, setTempWeightValue] = useState('');
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState('active');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    wholesale: '', // new wholesale price field
    sell_price: '',
    purchase_price: '',
    discount_price: '',
    category_id: '',
    brand_id: '',
    weight: '',
    stock_quantity: '',
    quantity_per_box: '',
    image_url: '',
    status: 'active'
  });

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    outOfStock: 0
  });

  // Helper function to format image URLs
  const formatImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    
    console.log('formatImageUrl input:', imageUrl);
    
    // If it's a blob URL, don't process it - it means upload failed and we're showing local preview
    if (imageUrl.startsWith('blob:')) {
      console.log('formatImageUrl output (blob - should not be used):', imageUrl);
      return ''; // Return empty to show placeholder instead of broken blob
    }
    
    // If it's already a full URL (http/https) or data URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      console.log('formatImageUrl output (already full):', imageUrl);
      return imageUrl;
    }
    
    // If it's just a filename, prepend the full path with products directory
    const fullUrl = `https://al-sadik.com/api/uploads/products/${imageUrl}`;
    console.log('formatImageUrl output (constructed):', fullUrl);
    return fullUrl;
  };

  // Function definitions (moved before useEffect to fix hoisting issues)
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      console.log('=== LOADING PRODUCTS ===');
      console.log('Starting loadProducts...');
      console.log('API_BASE_URL:', API_BASE_URL);
      console.log('Token:', adminAuthService.token ? adminAuthService.token.substring(0, 20) + '...' : 'NO TOKEN');
      
      const url = `${API_BASE_URL}/api/admin_products.php`;
      console.log('Requesting URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP Error Response Body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const text = await response.text();
      console.log('Raw response:', text.substring(0, 500) + '...');
      
      let data;
      try {
        data = JSON.parse(text);
        console.log('Parsed JSON:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid JSON response from server');
      }
      
      if (response.ok && data.success) {
        console.log('Success! Products loaded:', data.products?.length || 0);
        setProducts(data.products || []);
        calculateStats(data.products || []);
        setError(''); // Clear any previous errors
        console.log('Products state updated with', data.products ? data.products.length : 0, 'products');
      } else {
        setError(data.message || 'فشل في تحميل بيانات المنتجات');
      }
    } catch (error) {
      console.error('loadProducts error:', error);
      setError(`حدث خطأ في الاتصال بالخادم: ${error.message}`);
    } finally {
      setLoading(false);
      console.log('=== LOADING PRODUCTS END ===');
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      console.log('Loading categories...');
      const url = `${API_BASE_URL}/api/admin_categories.php`;
      console.log('Categories URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Categories response status:', response.status);
      const data = await response.json();
      console.log('Categories data:', data);
      
      if (response.ok && data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, []);

  const loadBrands = useCallback(async () => {
    try {
      console.log('Loading brands...');
      const url = `${API_BASE_URL}/api/admin_brands.php`;
      console.log('Brands URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Brands response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Brands data:', data);
      
      if (data.success) {
        setBrands(data.brands || []);
      } else {
        console.warn('Brands API returned success=false:', data.message);
      }
    } catch (error) {
      console.error('Error loading brands:', error);
      // Don't show error to user for brands loading failure, just log it
      // Set empty array so the component still works without brands
      setBrands([]);
    }
  }, []);

  const calculateStats = (productList) => {
    const total = productList.length;
    const active = productList.filter(p => 
      p.active === 1 || p.active === '1' || p.status === 'active'
    ).length;
    const inactive = productList.filter(p => 
      p.active === 0 || p.active === '0' || p.status === 'inactive'
    ).length;
    const outOfStock = productList.filter(p => parseInt(p.stock_quantity) === 0).length;

    setStats({ total, active, inactive, outOfStock });
  };

  const filterProducts = useCallback(() => {
    let filtered = products;

    if (searchQuery) {
      const trimmedQuery = searchQuery.trim();
      
      // Check if search query contains multiple SKUs separated by comma (not spaces)
      // Only treat as multi-SKU search if explicitly comma-separated
      if (trimmedQuery.includes(',')) {
        const skuTerms = trimmedQuery.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0);
        
        // Filter to show products with SKUs that match any of the comma-separated terms
        filtered = filtered.filter(product => 
          product.sku && skuTerms.includes(product.sku.toLowerCase())
        );
      } else {
        // Normal search - split by spaces and check if ALL words appear in name, description, or SKU
        const searchWords = trimmedQuery.toLowerCase().split(/\s+/).filter(word => word.length > 0);
        
        filtered = filtered.filter(product => {
          const productName = product.name?.toLowerCase() || '';
          const productDesc = product.description?.toLowerCase() || '';
          const productSku = product.sku?.toLowerCase() || '';
          const productBrand = product.brand?.toLowerCase() || '';
          const combinedText = `${productName} ${productDesc} ${productSku} ${productBrand}`;
          
          // Check if ALL search words appear in the combined text
          return searchWords.every(word => combinedText.includes(word));
        });
      }
    }

    if (categoryFilter) {
      filtered = filtered.filter(product => 
        product.category_id === parseInt(categoryFilter)
      );
    }

    if (brandFilter) {
      filtered = filtered.filter(product => 
        product.brand_id === parseInt(brandFilter)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(product => {
        if (statusFilter === 'active') {
          return product.active === 1 || product.active === '1' || product.status === 'active';
        } else if (statusFilter === 'inactive') {
          return product.active === 0 || product.active === '0' || product.status === 'inactive';
        }
        return true;
      });
    }

    setFilteredProducts(filtered);
  }, [products, searchQuery, categoryFilter, brandFilter, statusFilter]);

  useEffect(() => {
    console.log('ProductManagement useEffect triggered');
    console.log('Is authenticated:', adminAuthService.isAuthenticated());
    console.log('Current user:', adminAuthService.getCurrentUser());
    console.log('Token:', adminAuthService.token);
    
    if (!adminAuthService.isAuthenticated()) {
      console.log('Not authenticated, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    console.log('Authentication passed, loading data...');
    loadProducts();
    loadCategories();
    loadBrands();
  }, [navigate, loadProducts, loadCategories, loadBrands]);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, categoryFilter, brandFilter, statusFilter, filterProducts]);

  useEffect(() => {
    // Update selectAll state based on current page selections
    const currentPageProducts = filteredProducts
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const currentPageProductIds = currentPageProducts.map(p => p.id);
    
    if (currentPageProductIds.length === 0) {
      setSelectAll(false);
    } else {
      const allSelected = currentPageProductIds.every(id => selectedProducts.includes(id));
      setSelectAll(allSelected);
    }
  }, [selectedProducts, filteredProducts, page, rowsPerPage]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setCurrentProduct(product);
      // Clean any blob URLs from existing product data
      const cleanImageUrl = product.image_url && product.image_url.startsWith('blob:') ? '' : product.image_url;
      
      setFormData({
        name: product.name || '',
        description: product.description || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        wholesale: product.wholesale || '', // new wholesale price field
        sell_price: product.sell_price || '',
        purchase_price: product.purchase_price || '',
        discount_price: product.discount_price || '',
        category_id: product.category_id || '',
        brand_id: product.brand_id || '',
        weight: product.weight || '',
        stock_quantity: product.stock_quantity || '',
        quantity_per_box: product.quantity_per_box || '',
        image_url: cleanImageUrl || '',
        status: product.status || 'active'
      });
    } else {
      setCurrentProduct(null);
      setFormData({
        name: '',
        description: '',
        sku: '',
        barcode: '',
        wholesale: '', // new wholesale price field
        sell_price: '',
        purchase_price: '',
        discount_price: '',
        category_id: '',
        brand_id: '',
        weight: '',
        stock_quantity: '',
        quantity_per_box: '',
        image_url: '',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentProduct(null);
    setFormData({
      name: '',
      description: '',
      sku: '',
      barcode: '',
      wholesale: '', // wholesale price
      sell_price: '',
      purchase_price: '',
      discount_price: '',
      category_id: '',
      brand_id: '',
      weight: '',
      stock_quantity: '',
      quantity_per_box: '',
      image_url: '',
      status: 'active'
    });
  };

  const handleSaveProduct = async () => {
    if (saving) return; // Prevent double submission
    
    try {
      setSaving(true);
      // Prepare form data, extracting just filename from full image URLs if needed
      const processedFormData = { ...formData };
      
      // If image_url is a full URL to our server, extract just the filename
      if (processedFormData.image_url && processedFormData.image_url.includes('https://al-sadik.com/api/uploads/products/')) {
        processedFormData.image_url = processedFormData.image_url.split('/').pop();
      }
      
      const requestData = {
        action: currentProduct ? 'update' : 'create',
        ...processedFormData
      };

      if (currentProduct) {
        requestData.product_id = currentProduct.id;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(currentProduct ? 'تم تحديث المنتج بنجاح' : 'تم إضافة منتج جديد بنجاح');
        handleCloseDialog();
        loadProducts();
      } else {
        setError(data.message || 'فشل في حفظ بيانات المنتج');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      return;
    }

    try {
      console.log('=== DELETE PRODUCT DEBUG ===');
      console.log('Deleting product with ID:', productId, 'Type:', typeof productId);
      
      // Convert to number to ensure correct type
      const productIdNumber = parseInt(productId);
      console.log('Converted product ID to number:', productIdNumber);
      
      const requestData = {
        action: 'delete',
        product_id: productIdNumber,
        id: productIdNumber // Add both parameters in case backend expects 'id' instead of 'product_id'
      };
      
      console.log('Delete request data:', JSON.stringify(requestData, null, 2));
      console.log('API URL:', `${API_BASE_URL}/api/admin_products.php`);

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      console.log('Delete response status:', response.status);
      console.log('Delete response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('Delete response raw text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        setError('خطأ في استجابة الخادم - استجابة غير صالحة');
        return;
      }
      
      console.log('Delete response parsed:', JSON.stringify(data, null, 2));

      if (response.ok && data.success) {
        console.log('Delete API returned success, refreshing products...');
        setSuccess('تم حذف المنتج بنجاح');
        
        // Force refresh with a small delay to ensure backend processing is complete
        setTimeout(async () => {
          console.log('Refreshing products list...');
          await loadProducts();
          console.log('Products list refreshed');
        }, 500);
      } else {
        console.error('Delete failed:', data.message || 'Unknown error');
        setError(data.message || 'فشل في حذف المنتج');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
    console.log('=== DELETE PRODUCT DEBUG END ===');
  };

  // Handle restoring inactive products
  const handleRestoreProduct = async (productId) => {
    if (!window.confirm('هل أنت متأكد من استعادة هذا المنتج؟')) {
      return;
    }

    try {
      console.log('=== RESTORE PRODUCT ===');
      console.log('Restoring product with ID:', productId);
      
      const requestData = {
        action: 'update_status',
        product_id: parseInt(productId),
        status: 'active'
      };
      
      console.log('Restore request data:', JSON.stringify(requestData, null, 2));

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();
      console.log('Restore response:', data);

      if (response.ok && data.success) {
        setSuccess('تم استعادة المنتج بنجاح');
        loadProducts();
      } else {
        setError(data.message || 'فشل في استعادة المنتج');
      }
    } catch (error) {
      console.error('Restore error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  // Handle soft delete (set as inactive)
  const handleSoftDeleteProduct = async (productId) => {
    if (!window.confirm('هل أنت متأكد من إخفاء هذا المنتج؟ يمكن استعادته لاحقاً.')) {
      return;
    }

    try {
      console.log('=== SOFT DELETE PRODUCT ===');
      console.log('Soft deleting product with ID:', productId);
      
      const requestData = {
        action: 'soft_delete',
        product_id: parseInt(productId)
      };
      
      console.log('Soft delete request data:', JSON.stringify(requestData, null, 2));

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();
      console.log('Soft delete response:', data);

      if (response.ok && data.success) {
        setSuccess('تم إخفاء المنتج بنجاح');
        loadProducts();
      } else {
        setError(data.message || 'فشل في إخفاء المنتج');
      }
    } catch (error) {
      console.error('Soft delete error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  // Handle individual product selection
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Handle select all products
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      const currentPageProductIds = filteredProducts
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(product => product.id);
      setSelectedProducts(currentPageProductIds);
    }
    setSelectAll(!selectAll);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      setError('يرجى اختيار منتجات للحذف');
      return;
    }

    const confirmMessage = `هل أنت متأكد من حذف ${selectedProducts.length} منتج؟ هذا الإجراء لا يمكن التراجع عنه.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const deletePromises = selectedProducts.map(productId =>
        fetch(`${API_BASE_URL}/api/admin_products.php`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminAuthService.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'delete',
            product_id: productId,
            id: productId // Add both parameters in case backend expects 'id' instead of 'product_id'
          })
        }).then(response => response.json())
      );

      const results = await Promise.all(deletePromises);
      const successCount = results.filter(result => result.success).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        setSuccess(`تم حذف ${successCount} منتج بنجاح${failCount > 0 ? ` (فشل حذف ${failCount} منتج)` : ''}`);
        setSelectedProducts([]);
        setSelectAll(false);
        loadProducts();
      } else {
        setError('فشل في حذف المنتجات');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async (statusOverride = null) => {
    if (selectedProducts.length === 0) {
      setError('يرجى اختيار منتجات لتغيير حالتها');
      return;
    }

    const statusToApply = statusOverride || selectedBulkStatus;
    if (!['active', 'inactive'].includes(statusToApply)) {
      setError('حالة المنتج غير صحيحة');
      return;
    }

    try {
      setLoading(true);
      setBulkStatusDialogOpen(false);
      
      const statusPromises = selectedProducts.map(productId =>
        fetch(`${API_BASE_URL}/api/admin_products.php`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminAuthService.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'update_status',
            product_id: productId,
            status: statusToApply
          })
        }).then(response => response.json())
      );

      const results = await Promise.all(statusPromises);
      const successCount = results.filter(result => result.success).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        const statusLabel = statusToApply === 'active' ? 'نشط' : 'غير نشط';
        setSuccess(`تم تغيير حالة ${successCount} منتج إلى ${statusLabel} بنجاح` + (failCount > 0 ? ` (فشل ${failCount})` : ''));
        await loadProducts();
        setSelectedProducts([]);
        setSelectAll(false);
      } else {
        setError('فشل تغيير حالة المنتجات');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('=== Image Upload Debug ===');
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    console.log('API Base URL:', API_BASE_URL);
    console.log('Auth token (first 20 chars):', adminAuthService.token ? adminAuthService.token.substring(0, 20) + '...' : 'NO TOKEN');

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('type', 'products'); // Specify upload type for products

    try {
      setLoading(true);
      
      const uploadUrl = `${API_BASE_URL}/api/upload_image.php`;
      console.log('Uploading to:', uploadUrl);
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.log('Upload timeout - aborting request');
      }, 30000); // 30 second timeout
      
      // Upload image to dedicated image upload endpoint
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`
        },
        body: uploadFormData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('Response received, status:', response.status);

      const responseText = await response.text();
      console.log('Upload response status:', response.status);
      console.log('Upload response headers:', Object.fromEntries(response.headers.entries()));
      console.log('Upload response text:', responseText);
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          errorMessage += `\nRaw response: ${responseText}`;
        }
        throw new Error(errorMessage);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        throw new Error('Server returned invalid JSON response: ' + responseText);
      }

      console.log('Parsed upload result:', result);

      if (result.success) {
        const imageUrl = result.url || `https://al-sadik.com/api/uploads/products/${result.filename}`;
        console.log('Setting image URL to:', imageUrl);
        
        setFormData(prev => ({
          ...prev,
          image_url: imageUrl
        }));
        setSuccess('تم رفع الصورة بنجاح');
      } else {
        throw new Error(result.message || 'Upload failed - no success flag');
      }
    } catch (error) {
      console.error('=== Upload Error ===');
      console.error('Error details:', error);
      
      let errorMessage = 'خطأ في رفع الصورة: ';
      
      if (error.name === 'AbortError') {
        errorMessage += 'انتهت مهلة الرفع (30 ثانية). حاول مرة أخرى أو استخدم صورة أصغر.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'فشل في الاتصال بالخادم. تحقق من اتصالك بالإنترنت.';
      } else {
        errorMessage += error.message;
      }
      
      setError(errorMessage);
      // Do NOT set a blob URL fallback. Only use server path.
    } finally {
      setLoading(false);
      console.log('=== Upload Complete ===');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSelectedProducts([]);
    setSelectAll(false);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelectedProducts([]);
    setSelectAll(false);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'غير محدد';
  };

  // Handle inline category editing
  const handleCategoryEdit = (productId, currentCategoryId) => {
    setEditingCategory(productId);
    setTempCategoryValue(currentCategoryId || '');
  };

  const handleCategoryCancel = () => {
    setEditingCategory(null);
    setTempCategoryValue('');
  };

  const handleCategorySave = async (productId) => {
    try {
      // Get the current product data
      const product = products.find(p => p.id === productId);
      if (!product) {
        setError('لم يتم العثور على المنتج');
        return;
      }

      // Use the standard update action with all product data
      const requestData = {
        action: 'update',
        product_id: productId,
        name: product.name,
        description: product.description || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        wholesale: product.wholesale || '',
        sell_price: product.sell_price || '',
        purchase_price: product.purchase_price || '',
        discount_price: product.discount_price || '',
        category_id: tempCategoryValue, // This is the updated category
        brand_id: product.brand_id || '',
        weight: product.weight || '',
        stock_quantity: product.stock_quantity || '',
        quantity_per_box: product.quantity_per_box || '',
        image_url: product.image_url || '',
        status: product.status || 'active'
      };

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update local state
        setProducts(prevProducts => 
          prevProducts.map(product => 
            product.id === productId 
              ? { ...product, category_id: tempCategoryValue }
              : product
          )
        );
        setSuccess('تم تحديث الفئة بنجاح');
      } else {
        setError(data.message || 'فشل في تحديث الفئة');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setEditingCategory(null);
      setTempCategoryValue('');
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : 'غير محدد';
  };

  // Handle inline brand editing
  const handleBrandEdit = (productId, currentBrandId) => {
    setEditingBrand(productId);
    setTempBrandValue(currentBrandId || '');
  };

  const handleBrandCancel = () => {
    setEditingBrand(null);
    setTempBrandValue('');
  };

  const handleBrandSave = async (productId) => {
    try {
      // Get the current product data
      const product = products.find(p => p.id === productId);
      if (!product) {
        setError('لم يتم العثور على المنتج');
        return;
      }

      // Use the standard update action with all product data
      const requestData = {
        action: 'update',
        product_id: productId,
        name: product.name,
        description: product.description || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        wholesale: product.wholesale || '',
        sell_price: product.sell_price || '',
        purchase_price: product.purchase_price || '',
        discount_price: product.discount_price || '',
        category_id: product.category_id || '',
        brand_id: tempBrandValue, // Updated brand
        weight: product.weight || '',
        stock_quantity: product.stock_quantity || '',
        quantity_per_box: product.quantity_per_box || '',
        image_url: product.image_url || '',
        status: product.status || 'active'
      };

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('تم تحديث الماركة بنجاح');
        // Update local state
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === productId ? { ...p, brand_id: tempBrandValue } : p
          )
        );
      } else {
        setError(data.message || 'فشل في تحديث الماركة');
      }
    } catch (error) {
      console.error('Error updating brand:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setEditingBrand(null);
      setTempBrandValue('');
    }
  };

  // Handle inline weight editing
  const handleWeightEdit = (productId, currentWeight) => {
    setEditingWeight(productId);
    setTempWeightValue(currentWeight || '');
  };

  const handleWeightCancel = () => {
    setEditingWeight(null);
    setTempWeightValue('');
  };

  const handleWeightSave = async (productId) => {
    try {
      // Get the current product data
      const product = products.find(p => p.id === productId);
      if (!product) {
        setError('المنتج غير موجود');
        return;
      }

      // Use the standard update action with all product data
      const requestData = {
        action: 'update',
        product_id: productId,
        name: product.name,
        description: product.description || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        wholesale: product.wholesale || '',
        sell_price: product.sell_price || '',
        purchase_price: product.purchase_price || '',
        discount_price: product.discount_price || '',
        category_id: product.category_id || '',
        brand_id: product.brand_id || '',
        weight: tempWeightValue, // Update weight with the new value
        stock_quantity: product.stock_quantity || '',
        quantity_per_box: product.quantity_per_box || '',
        image_url: product.image_url || '',
        status: product.status || 'active'
      };

      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('تم تحديث الوزن بنجاح');
        setTimeout(() => setSuccess(''), 3000);
        
        // Reload products to get updated data
        await loadProducts();
        
        // Reset editing state
        setEditingWeight(null);
        setTempWeightValue('');
      } else {
        setError(data.message || 'حدث خطأ في تحديث الوزن');
      }
    } catch (error) {
      console.error('Error updating weight:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setEditingWeight(null);
      setTempWeightValue('');
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setBrandFilter('');
    setStatusFilter('');
    setSelectedProducts([]);
    setSelectAll(false);
    setPage(0);
  };

  // Export products to CSV
  const handleExportProducts = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/api/export_products.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('فشل في تصدير البيانات');
      }

      // Get the CSV content
      const csvContent = await response.text();
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('تم تصدير البيانات بنجاح');
    } catch (error) {
      console.error('Export error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Import products from CSV
  const handleImportProducts = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('يرجى اختيار ملف CSV فقط');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('csv_file', file);

      const response = await fetch(`${API_BASE_URL}/api/import_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.token}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'فشل في استيراد البيانات');
      }

      // Create success message based on imported and updated counts
      let successMessage = '';
      const imported = result.imported || 0;
      const updated = result.updated || 0;
      const skipped = result.skipped || 0;

      if (imported > 0 && updated > 0) {
        successMessage = `تم إضافة ${imported} منتج جديد وتحديث ${updated} منتج موجود بنجاح`;
      } else if (imported > 0) {
        successMessage = `تم إضافة ${imported} منتج جديد بنجاح`;
      } else if (updated > 0) {
        successMessage = `تم تحديث ${updated} منتج موجود بنجاح`;
      } else {
        successMessage = 'لم يتم معالجة أي منتج';
      }

      if (skipped > 0) {
        successMessage += ` (تم تجاهل ${skipped} منتج)`;
      }

      if (imported + updated > 0) {
        setSuccess(successMessage);
      } else {
        setError(successMessage);
      }
      
      // Refresh products list
      loadProducts();

    } catch (error) {
      console.error('Import error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const csvHeader = [
      'ID',
      'اسم المنتج',
      'الوصف',
      'كود المنتج (SKU)',
      'الباركود',
      'معرف العلامة التجارية',
      'معرف الفئة',
      'اسم الفئة',
      'الوزن',
      'الكمية',
      'الكمية في الصندوق',
      'سعر الشراء (IQD)',
      'نسبة الربح (%)',
      'سعر البيع (IQD)',
      'سعر الجملة (IQD)',
      'نشط',
      'الصورة',
      'السعر (IQD)',
      'العلامة التجارية',
      'سعر الخصم (IQD)',
      'نسبة الخصم (%)',
      'السعر بعد الخصم (IQD)'
    ];

    // Sample data
    const sampleData = [
      [
        '', // ID - leave empty for new products
        'منتج تجريبي',
        'وصف المنتج التجريبي',
        'SKU001',
        '1234567890',
        '1', // brand_id
        '1', // category_id
        'إلكترونيات',
        '0.5 كغ',
        '100',
        '12',
        '20000',
        '25.00',
        '25000',
        '22000', // wholesale price
        '1',
        'https://al-sadik.com/api/uploads/sample.jpg',
        '25000',
        'سامسونج',
        '0',
        '0',
        '25000'
      ]
    ];

    // Create CSV content
    const csvContent = [
      csvHeader,
      ...sampleData
    ].map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Add BOM for proper UTF-8 encoding
    const csvWithBom = '\ufeff' + csvContent;

    // Create download
    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'products_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#4169E1' }}>
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: { xs: 0.5, sm: 2 } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <ProductsIcon sx={{ mr: { xs: 0.5, sm: 2 }, display: { xs: 'none', sm: 'block' } }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            إدارة المنتجات
          </Typography>
          
          {/* Template Download Button */}
          <IconButton
            color="inherit"
            onClick={handleDownloadTemplate}
            size="small"
            sx={{ mr: 0.5, display: { xs: 'inline-flex', md: 'none' } }}
            title="تحميل القالب"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
            sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
          >
            قالب
          </Button>
          
          {/* Export Button */}
          <IconButton
            color="inherit"
            onClick={handleExportProducts}
            disabled={loading}
            size="small"
            sx={{ mr: 0.5, display: { xs: 'inline-flex', md: 'none' } }}
            title="تصدير"
          >
            <ExportIcon fontSize="small" />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<ExportIcon />}
            onClick={handleExportProducts}
            disabled={loading}
            sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
          >
            تصدير
          </Button>
          
          {/* Import Button */}
          <IconButton
            color="inherit"
            component="label"
            disabled={loading}
            size="small"
            sx={{ mr: 0.5, display: { xs: 'inline-flex', md: 'none' } }}
            title="استيراد"
          >
            <ImportIcon fontSize="small" />
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleImportProducts}
            />
          </IconButton>
          <Button
            color="inherit"
            component="label"
            startIcon={<ImportIcon />}
            disabled={loading}
            sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
          >
            استيراد
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleImportProducts}
            />
          </Button>
          
          {/* Bulk Operations Buttons */}
          {selectedProducts.length > 0 && (
            <>
              {/* Bulk Activate Button */}
              <IconButton
                color="success"
                onClick={() => handleBulkStatusChange('active')}
                disabled={loading}
                size="small"
                sx={{ mr: 0.5, display: { xs: 'inline-flex', sm: 'none' } }}
                title={`تفعيل (${selectedProducts.length})`}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
              <Button
                color="success"
                startIcon={<RestoreIcon />}
                onClick={() => handleBulkStatusChange('active')}
                disabled={loading}
                sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
                size="small"
              >
                تفعيل المحدد ({selectedProducts.length})
              </Button>

              {/* Bulk Deactivate Button */}
              <IconButton
                color="warning"
                onClick={() => handleBulkStatusChange('inactive')}
                disabled={loading}
                size="small"
                sx={{ mr: 0.5, display: { xs: 'inline-flex', sm: 'none' } }}
                title={`تعطيل (${selectedProducts.length})`}
              >
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
              <Button
                color="warning"
                startIcon={<VisibilityOffIcon />}
                onClick={() => handleBulkStatusChange('inactive')}
                disabled={loading}
                sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
                size="small"
              >
                تعطيل المحدد ({selectedProducts.length})
              </Button>
              
              {/* Bulk Delete Button */}
              <IconButton
                color="error"
                onClick={handleBulkDelete}
                disabled={loading}
                size="small"
                sx={{ mr: 0.5, display: { xs: 'inline-flex', sm: 'none' } }}
                title={`حذف (${selectedProducts.length})`}
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
              <Button
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={handleBulkDelete}
                disabled={loading}
                sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
                size="small"
              >
                حذف ({selectedProducts.length})
              </Button>
            </>
          )}
          
          {/* Add Product Button */}
          <IconButton
            color="inherit"
            onClick={() => handleOpenDialog()}
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            title="إضافة منتج جديد"
          >
            <AddIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            size="small"
          >
            إضافة منتج
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4169E1', mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            إدارة المنتجات
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            يمكنك من هنا إضافة وتعديل وحذف المنتجات في المتجر. استخدم المرشحات لعرض المنتجات النشطة أو غير النشطة.
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <ProductsIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#4169E1', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {stats.total}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  إجمالي المنتجات
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <ViewIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#4caf50', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {stats.active}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  منتجات نشطة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <PriceIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#ff9800', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {stats.inactive}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  منتجات غير نشطة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <ImageIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#f44336', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {stats.outOfStock}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  نفدت من المخزن
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Search and Filters */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="البحث بالاسم، الوصف، أو رمز التخزين (SKU)... (أدخل عدة SKUs مفصولة بفاصلة أو مسافة)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>تصفية حسب الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="تصفية حسب الحالة"
                >
                  <MenuItem value="">كل الحالات</MenuItem>
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>تصفية حسب الفئة</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="تصفية حسب الفئة"
                >
                  <MenuItem value="">كل الفئات</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>تصفية حسب الماركة</InputLabel>
                <Select
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  label="تصفية حسب الماركة"
                >
                  <MenuItem value="">كل الماركات</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Clear Filters Button */}
          {(searchQuery || categoryFilter || brandFilter || statusFilter) && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                size="small"
              >
                مسح كل المرشحات
              </Button>
            </Box>
          )}
        </Paper>

        {/* Products Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ px: { xs: 0.5, sm: 2 } }}>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>الصورة</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: { xs: 120, sm: 150 } }}>الاسم</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: { xs: 100, sm: 150 } }}>الفئة</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>الماركة</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>السعر</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>الوزن</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>المخزون</TableCell>
                  <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>الحالة</TableCell>
                  <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      جاري التحميل...
                    </TableCell>
                  </TableRow>
                ) : filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      لا توجد منتجات
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => (
                    <TableRow key={product.id}>
                      <TableCell padding="checkbox" sx={{ px: { xs: 0.5, sm: 2 } }}>
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => handleSelectProduct(product.id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 } }}>
                        <Avatar
                          src={formatImageUrl(product.image_url)}
                          alt={product.name}
                          sx={{ width: { xs: 35, sm: 50 }, height: { xs: 35, sm: 50 } }}
                        >
                          <ImageIcon sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }} />
                        </Avatar>
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{product.name}</TableCell>
                      <TableCell>
                        {editingCategory === product.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={tempCategoryValue}
                                onChange={(e) => setTempCategoryValue(e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">بدون فئة</MenuItem>
                                {categories.map((category) => (
                                  <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleCategorySave(product.id)}
                              title="حفظ"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={handleCategoryCancel}
                              title="إلغاء"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                              padding: '4px 8px',
                              borderRadius: 1
                            }}
                            onClick={() => handleCategoryEdit(product.id, product.category_id)}
                            title="انقر لتعديل الفئة"
                          >
                            <Typography variant="body2">
                              {getCategoryName(product.category_id)}
                            </Typography>
                            <EditIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        {editingBrand === product.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={tempBrandValue}
                                onChange={(e) => setTempBrandValue(e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">بدون ماركة</MenuItem>
                                {brands.map((brand) => (
                                  <MenuItem key={brand.id} value={brand.id}>
                                    {brand.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleBrandSave(product.id)}
                              title="حفظ"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={handleBrandCancel}
                              title="إلغاء"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                              padding: '4px 8px',
                              borderRadius: 1
                            }}
                            onClick={() => handleBrandEdit(product.id, product.brand_id)}
                            title="انقر لتعديل الماركة"
                          >
                            <Typography variant="body2">
                              {getBrandName(product.brand_id)}
                            </Typography>
                            <EditIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{product.sell_price || product.price}د.ع</TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 }, display: { xs: 'none', lg: 'table-cell' } }}>
                        {editingWeight === product.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={tempWeightValue}
                              onChange={(e) => setTempWeightValue(e.target.value)}
                              sx={{ width: 100 }}
                              InputProps={{
                                endAdornment: <span style={{ fontSize: '0.75rem', color: '#666' }}>kg</span>
                              }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleWeightSave(product.id)}
                              title="حفظ"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={handleWeightCancel}
                              title="إلغاء"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                              padding: '4px 8px',
                              borderRadius: 1
                            }}
                            onClick={() => handleWeightEdit(product.id, product.weight)}
                            title="انقر لتعديل الوزن"
                          >
                            <Typography variant="body2">
                              {product.weight ? `${product.weight} kg` : 'غير محدد'}
                            </Typography>
                            <EditIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={product.stock_quantity}
                          color={parseInt(product.stock_quantity) === 0 ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={product.status === 'active' ? 'نشط' : 'غير نشط'}
                          color={product.status === 'active' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 } }}>
                        {/* Edit button - always available */}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(product)}
                          title="تعديل"
                        >
                          <EditIcon />
                        </IconButton>
                        
                        {/* Show different actions based on product status */}
                        {(product.active === 1 || product.active === '1' || product.status === 'active') ? (
                          <>
                            {/* Active product actions */}
                            <IconButton
                              size="small"
                              onClick={() => handleSoftDeleteProduct(product.id)}
                              title="إخفاء المنتج"
                              color="warning"
                            >
                              <VisibilityOffIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteProduct(product.id)}
                              title="حذف نهائي"
                              color="error"
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            {/* Inactive product actions */}
                            <IconButton
                              size="small"
                              onClick={() => handleRestoreProduct(product.id)}
                              title="استعادة المنتج"
                              color="success"
                            >
                              <RestoreIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteProduct(product.id)}
                              title="حذف نهائي"
                              color="error"
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredProducts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="عدد الصفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} من أصل ${count !== -1 ? count : `أكثر من ${to}`}`
            }
          />
        </Paper>
      </Container>

      {/* Add/Edit Product Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>
          {currentProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Product Name */}
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                label="اسم المنتج"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange('name')}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="وصف المنتج"
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange('description')}
              />
            </Grid>
            
            {/* SKU */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="رمز التخزين (SKU)"
                fullWidth
                variant="outlined"
                value={formData.sku}
                onChange={handleInputChange('sku')}
              />
            </Grid>
            
            {/* Barcode */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="الباركود"
                fullWidth
                variant="outlined"
                value={formData.barcode}
                onChange={handleInputChange('barcode')}
              />
            </Grid>
            
            {/* Purchase Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="سعر الشراء"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.purchase_price}
                onChange={handleInputChange('purchase_price')}
                InputProps={{
                  endAdornment: <span style={{ color: '#666' }}>IQD</span>
                }}
              />
            </Grid>
            
            {/* Wholesale Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="سعر الجملة"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.wholesale}
                onChange={handleInputChange('wholesale')}
                helperText="سعر الجملة للبيع بكميات كبيرة"
                InputProps={{
                  endAdornment: <span style={{ color: '#666' }}>IQD</span>
                }}
              />
            </Grid>
            
            {/* Sell Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="سعر التجزئة"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.sell_price}
                onChange={handleInputChange('sell_price')}
                helperText="سعر التجزئة للبيع للأفراد"
                InputProps={{
                  endAdornment: <span style={{ color: '#666' }}>IQD</span>
                }}
              />
            </Grid>
            
            {/* Discount Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="مبلغ الخصم"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.discount_price}
                onChange={handleInputChange('discount_price')}
                helperText="مبلغ الخصم بالدينار العراقي (اتركه فارغ إذا لم يكن هناك خصم)"
                InputProps={{
                  endAdornment: <span style={{ color: '#666' }}>IQD</span>
                }}
              />
            </Grid>
            
            {/* Weight */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="الوزن"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.weight}
                onChange={handleInputChange('weight')}
                helperText="الوزن بالكيلوجرام (للحساب توصيل)"
                InputProps={{
                  endAdornment: <span style={{ color: '#666' }}>kg</span>
                }}
              />
            </Grid>
            
            {/* Stock Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="كمية المخزون"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.stock_quantity}
                onChange={handleInputChange('stock_quantity')}
              />
            </Grid>
            
            {/* Quantity per Box */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="الكمية في الكرتونة"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.quantity_per_box}
                onChange={handleInputChange('quantity_per_box')}
              />
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>الفئة</InputLabel>
                <Select
                  value={formData.category_id || ''}
                  onChange={handleInputChange('category_id')}
                  label="الفئة"
                >
                  <MenuItem value="">بدون فئة</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Brand */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>الماركة</InputLabel>
                <Select
                  value={formData.brand_id || ''}
                  onChange={handleInputChange('brand_id')}
                  label="الماركة"
                >
                  <MenuItem value="">بدون ماركة</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={formData.status || 'active'}
                  onChange={handleInputChange('status')}
                  label="الحالة"
                >
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Image Upload */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  صورة المنتج
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<ImageIcon />}
                    sx={{ mb: 1 }}
                  >
                    اختر صورة
                  </Button>
                </label>
                {formData.image_url && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar 
                      src={formatImageUrl(formData.image_url)} 
                      sx={{ width: 50, height: 50 }}
                    >
                      <ImageIcon />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary">
                      معاينة الصورة
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Fallback: Manual URL Input */}
              <TextField
                margin="dense"
                label="أو أدخل رابط الصورة يدوياً"
                fullWidth
                variant="outlined"
                value={formData.image_url}
                onChange={handleInputChange('image_url')}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button 
            onClick={handleSaveProduct} 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'جاري الحفظ...' : (currentProduct ? 'تحديث' : 'إضافة')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Status Change Dialog */}
      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>تغيير حالة المنتجات المحددة</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              سيتم تغيير حالة {selectedProducts.length} منتج
            </Typography>
            <FormControl fullWidth>
              <InputLabel>الحالة الجديدة</InputLabel>
              <Select
                value={selectedBulkStatus}
                onChange={(e) => setSelectedBulkStatus(e.target.value)}
                label="الحالة الجديدة"
              >
                <MenuItem value="active">نشط</MenuItem>
                <MenuItem value="inactive">غير نشط</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleBulkStatusChange}
            variant="contained"
            disabled={loading}
          >
            تطبيق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
